// src/pages/OurWork.jsx
import { motion } from "framer-motion";
import { CheckCircle, Leaf, HeartHandshake } from "lucide-react";

// Import images
import ourWorkHero from "../assets/slide5.jpg";
import project1 from "../assets/slide1.jpg";
import project2 from "../assets/slide2.jpg";
import project3 from "../assets/slide3.jpg";

export default function OurWork() {
  const pastWork = [
    {
      title: "Rescued 500+ Injured Animals",
      description:
        "From elephants to stray dogs, our rescue missions have saved countless lives.",
      icon: <CheckCircle className="w-8 h-8 text-green-600" />,
    },
    {
      title: "Planted 10,000+ Trees",
      description:
        "We organized plantation drives across urban and rural areas to restore ecosystems.",
      icon: <Leaf className="w-8 h-8 text-green-600" />,
    },
    {
      title: "Educated 2000+ Children",
      description:
        "Awareness programs in schools and villages to spread knowledge on wildlife & environment.",
      icon: <HeartHandshake className="w-8 h-8 text-green-600" />,
    },
  ];

  const currentProjects = [
    {
      image: project1,
      title: "Elephant Conservation Program",
      desc: "Tracking and protecting wild elephants from illegal poaching.",
    },
    {
      image: project2,
      title: "Urban Wildlife Rescue",
      desc: "24/7 helpline and rapid rescue team for city-based wildlife in distress.",
    },
    {
      image: project3,
      title: "Community Forest Initiative",
      desc: "Empowering local communities to protect and manage forests.",
    },
  ];

  return (
    <div className="bg-white text-gray-900">
      {/* Hero Section */}
      <div className="relative h-[60vh] bg-black">
        <img
          src={ourWorkHero}
          alt="Our Work"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="relative z-10 flex items-center justify-center h-full text-center">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-4xl md:text-6xl font-bold text-lime-400 drop-shadow-lg"
          >
            Our Work – Past & Present
          </motion.h1>
        </div>
      </div>

      {/* Past Achievements */}
      <section className="max-w-6xl mx-auto py-16 px-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          Past Achievements
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {pastWork.map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="p-6 bg-gray-50 rounded-2xl shadow-lg flex flex-col items-center text-center"
            >
              {item.icon}
              <h3 className="mt-4 text-xl font-semibold">{item.title}</h3>
              <p className="mt-2 text-gray-600">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Current Projects */}
      <section className="bg-gray-100 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Ongoing Projects
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {currentProjects.map((project, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.03 }}
                className="rounded-2xl overflow-hidden shadow-lg bg-white"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold">{project.title}</h3>
                  <p className="mt-2 text-gray-600">{project.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-lime-600 text-center text-white">
        <h2 className="text-3xl font-bold mb-4">
          Together, We Can Make a Difference 🌍
        </h2>
        <p className="mb-8 max-w-2xl mx-auto">
          Support our initiatives and help us continue making an impact in
          wildlife conservation and community development.
        </p>
        <motion.a
          href="/donate"
          whileHover={{ scale: 1.1 }}
          className="bg-white text-lime-600 font-bold px-8 py-4 rounded-full shadow-lg"
        >
          Donate Now
        </motion.a>
      </section>
    </div>
  );
}
